import GamedayApiService from "./gameday-api-service";
import { sanitizeInvalidCharacters } from "./common/string-util";
import { eventRepository, gameRepository } from "../storage/storage";
import EventService from "./event-service";
import EventStatus, { EventStatusCode } from "../sports/common/event-status";
import ContextService from "./context-service";
import TennisSerializer from "./serializers/TennisSerializer";
import GameService from "./game-service";

class TennisService {
    async _finalizeEvent(eventId) {
        return EventService.setEventStatus(eventId, EventStatusCode.FINAL);
    }

    async _finalizeGameContext(gameId, finalizeSettings) {
        let gameContext = await ContextService.getGameContext(gameId);

        const { finalizeOrder, ...rest } = finalizeSettings || {};
        gameContext.finalizeSettings = {
            ...gameContext.finalizeSettings,
            ...rest
        };

        return ContextService.updateContext(gameContext);
    }

    async updateMatchFinalizationOrder(gameId, isSingle, orderedIds) {
        let gameContext = await ContextService.getGameContext(gameId);
        if (isSingle) {
            gameContext.finalizeSettings.finalizeOrder.singles = orderedIds;
        } else {
            gameContext.finalizeSettings.finalizeOrder.doubles = orderedIds;
        }
        gameContext = await ContextService.updateContext(gameContext);

        return TennisSerializer.serializeGameContext(gameContext);
    }

    async getPlayers(externalTeamId, includeDeleted = false) {
        return GamedayApiService.getPlayers(externalTeamId, includeDeleted);
    }

    async finalizeGame(gameId, finalizeSettings) {
        const game = await gameRepository.getById(gameId);
        const { eventId } = game;
        const { notes, ...additionalSettings } = finalizeSettings;

        const gameContext = await this._finalizeGameContext(
            gameId,
            additionalSettings
        );
        const event = await this._finalizeEvent(eventId);

        event.notes = sanitizeInvalidCharacters(notes);
        event.score.home = gameContext.getHomeScore();
        event.score.away = gameContext.getVisitorScore();
        await GamedayApiService.saveOrUpdateEvent(event);

        return {
            event,
            gameContext: TennisSerializer.serializeGameContext(gameContext)
        };
    }

    async saveFinalizationSettings(gameId, finalizeSettings) {
        const game = await gameRepository.getById(gameId);
        let gameContext = await ContextService.getGameContext(gameId);
        const { finalizeOrder, ...rest } = finalizeSettings || {};

        gameContext.finalizeSettings = {
            ...gameContext.finalizeSettings,
            ...rest
        };

        gameContext = await ContextService.updateContext(gameContext);

        const event = await eventRepository.getById(game.eventId);
        event.notes = sanitizeInvalidCharacters(finalizeSettings.notes);
        await GamedayApiService.saveOrUpdateEvent(event);

        return {
            event,
            gameContext: TennisSerializer.serializeGameContext(gameContext)
        };
    }

    async startGame(eventId) {
        const game = await GameService.getGameByEventId(eventId);
        const gameContext = await ContextService.getGameContext(game.id);
        gameContext.gameInitialized = true;
        await ContextService.updateContext(gameContext);

        let event = await eventRepository.getById(eventId);
        if (!EventStatus.isStarted(event)) {
            await EventService.setEventStarted(eventId);
        }
    }

    async isGameDataComplete() {
        return { isComplete: true, missingGameData: [] };
    }
}

export default new TennisService();
